import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { Instagram, Linkedin, Mail, Phone, MapPin, Youtube, ArrowUpRight, ArrowUp, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDuyurular } from '../../hooks/useSupabaseData';

import { HERO_VIDEO, HERO_POSTER } from '../../utils/heroVideo';
import { localizePath, toEnPath, toTrPath } from '../../utils/locale';
const BRAND = '#7B0D1E';

// X (Twitter) logosu — lucide'deki eski kuş ikonu yerine
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const PHONES = ['(+90312) 299 20 80', '(+90312) 297 68 50', '(+90312) 297 68 51', '(+90312) 299 20 93'];

const SOCIALS = [
  { href: 'https://www.instagram.com/hacettepefenfakultesi', label: 'Instagram', icon: <Instagram size={17} /> },
  { href: 'https://x.com/hacettepefenfak', label: 'X', icon: <XIcon size={15} /> },
  { href: 'https://www.youtube.com/@hacettepeunifenfakultesi', label: 'YouTube', icon: <Youtube size={18} /> },
  { href: 'https://tr.linkedin.com/in/fen-fak%C3%BCltesi-hacettepe-%C3%BCniversitesi-32775a234', label: 'LinkedIn', icon: <Linkedin size={17} /> },
];

// Fakülte (Hacettepe amblemli) logosu alt şeritte yerel dosyadan gösterilir; burada yalnızca ek kurum logoları var
const INSTITUTION_LOGOS = [
  {
    src: 'https://iysportal.hacettepe.edu.tr/templates/template3/images/yokak5.svg',
    alt: 'YÖKAK',
    href: 'https://www.yokak.gov.tr',
    className: 'h-9',
  },
];

const telHref = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

const ColumnTitle = ({ children }) => (
  <h3 className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-gray-900 mb-6">
    <span className="h-px w-6" style={{ backgroundColor: BRAND }} />
    {children}
  </h3>
);

// Ana sayfadaki HeroSection ile aynı rakamlar
const StatsRow = ({ t }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const stats = [
    { num: 2627, suffix: '+', label: t('home.stats.undergraduateStudents') },
    { num: 511, suffix: '+', label: t('home.stats.graduateStudents') },
    { num: 183, suffix: '', label: t('home.stats.facultyMembers') },
    { num: 103, suffix: '+', label: t('home.stats.activeProjects') },
    { num: 15824, suffix: '+', label: t('home.stats.totalAlumni') },
  ];

  return (
    <div ref={ref} className="grid grid-cols-2 md:grid-cols-5 gap-y-8 border-b border-gray-200 pb-12 mb-14">
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={`text-center px-2 ${i > 0 ? 'md:border-l md:border-gray-200' : ''} ${i === 4 ? 'col-span-2 md:col-span-1' : ''}`}
        >
          <div
            className="text-4xl md:text-5xl font-bold leading-none"
            style={{ color: BRAND, fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            {inView ? <CountUp end={s.num} duration={2.5} separator="." /> : 0}
            {s.suffix}
          </div>
          <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">{s.label}</div>
        </div>
      ))}
    </div>
  );
};

const AnniversaryBand = ({ t, prefix }) => {
  // Video yalnızca band ekrana yaklaşınca yüklenir; her sayfanın sonunda boşuna indirilmez
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '300px 0px' });

  return (
  <div ref={ref} className="relative overflow-hidden bg-[#3A0610]">
    {/* Fakülte kampüs videosu (sitenin giriş bölümlerinde kullanılan) */}
    {inView && (
      <video
        className="absolute inset-0 w-full h-full object-cover"
        data-src={HERO_VIDEO || undefined}
        poster={HERO_POSTER}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
      />
    )}
    {/* Bordo katman: yazıların okunması ve marka rengi için */}
    <div
      className="absolute inset-0"
      style={{ background: `linear-gradient(100deg, rgba(58,6,16,0.95) 0%, rgba(123,13,30,0.82) 45%, rgba(123,13,30,0.55) 100%)` }}
    />
    {/* İnce çizgi deseni */}
    <div
      className="absolute inset-0 opacity-[0.06]"
      style={{ backgroundImage: 'repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 22px)' }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 flex flex-col md:flex-row items-center gap-8 md:gap-12">
      <div
        className="text-[110px] md:text-[150px] font-black leading-[0.8] select-none flex-shrink-0"
        style={{ color: 'transparent', WebkitTextStroke: '2px rgba(255,255,255,0.85)', fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        aria-hidden="true"
      >
        60
      </div>
      <div className="flex-1 text-center md:text-left">
        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/60">{t('footer.facultyFullName')}</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
          {t('footer.anniversaryTitle')}
        </h2>
        <p className="mt-2 text-white/75 max-w-xl">{t('footer.anniversaryText')}</p>
      </div>
      <div className="flex flex-wrap justify-center gap-3 flex-shrink-0">
        <Link
          to={localizePath('/kurumsal/tarihce')}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#7B0D1E] text-sm font-semibold hover:bg-white/90 hover:gap-3 transition-all"
        >
          {t('footer.ourHistory')}
          <ArrowRight size={16} />
        </Link>
        <Link
          to={localizePath('/iletisim')}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/50 text-white text-sm font-semibold hover:bg-white/10 hover:gap-3 transition-all"
        >
          {t('footer.getInTouch')}
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </div>
  );
};

const announcementHref = (d, prefix) => {
  if (d.link?.startsWith('http')) return d.link;
  if (d.slug) return localizePath(`/duyurular/${d.slug}`);
  if (d.id) return localizePath(`/duyurular/${d.id}`);
  return localizePath('/duyurular');
};

const LatestAnnouncements = ({ t, prefix, isEn }) => {
  const { duyurular } = useDuyurular();
  const latest = useMemo(() =>
    [...duyurular]
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 3),
  [duyurular]);
  const locale = isEn ? 'en-GB' : 'tr-TR';

  return (
    <div>
      <ColumnTitle>{t('footer.latestAnnouncements')}</ColumnTitle>
      <ul className="space-y-4">
        {latest.map((d) => {
          const dateObj = d.date ? new Date(d.date) : null;
          const href = announcementHref(d, prefix);
          const isExt = href.startsWith('http');
          const inner = (
            <>
              <div className="flex-shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center border border-[#7B0D1E]/15 bg-white group-hover:bg-[#7B0D1E] transition-colors">
                <span className="text-lg font-bold leading-none text-[#7B0D1E] group-hover:text-white transition-colors" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                  {dateObj ? dateObj.toLocaleDateString(locale, { day: '2-digit' }) : '—'}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-wide text-[#7B0D1E]/80 group-hover:text-white/90 transition-colors mt-0.5">
                  {dateObj ? dateObj.toLocaleDateString(locale, { month: 'short' }).replace('.', '') : ''}
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-snug line-clamp-2 group-hover:text-[#7B0D1E] transition-colors">
                {d.title}
              </p>
            </>
          );
          return (
            <li key={d.id || d.title}>
              {isExt ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3">{inner}</a>
              ) : (
                <Link to={href} className="group flex items-center gap-3">{inner}</Link>
              )}
            </li>
          );
        })}
      </ul>
      <Link
        to={localizePath('/duyurular')}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7B0D1E] hover:gap-2.5 transition-all"
      >
        {t('footer.allAnnouncements')}
        <ArrowRight size={15} />
      </Link>
    </div>
  );
};

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isEn = (i18n.language || '').startsWith('en');
  const prefix = isEn ? '/en' : '';
  const location = useLocation();
  const navigate = useNavigate();

  const departments = [
    { name: t('footer.actuarialScience'), href: 'https://aktuerya.hacettepe.edu.tr/', phone: '(+90312) 297 79 98' },
    { name: t('footer.biology'), href: 'https://biology.hacettepe.edu.tr/', phone: '(+90312) 297 80 00' },
    { name: t('footer.statistics'), href: 'https://stat.hacettepe.edu.tr/', phone: '(+90312) 297 79 00' },
    { name: t('footer.chemistry'), href: 'https://chem.hacettepe.edu.tr/', phone: '(+90312) 297 79 40' },
    { name: t('footer.mathematics'), href: 'https://mat.hacettepe.edu.tr/', phone: '(+90312) 297 78 50' },
  ];

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Navbar'daki dil değiştirme mantığıyla aynı (eşleşmeler: config/enSlugs.js)
  const changeLanguage = (lang) => {
    const path = location.pathname + location.search;
    navigate(lang === 'en' ? toEnPath(path) : toTrPath(path));
  };

  return (
    <footer className="bg-[#FAF7F5] text-gray-700">
      {/* 60. yıl bandı */}
      <AnniversaryBand t={t} prefix={prefix} />

      <div className="relative overflow-hidden">
      {/* Dekoratif arka plan */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-[0.07]" style={{ backgroundColor: BRAND }} />
        <div className="absolute top-1/2 -left-32 w-80 h-80 rounded-full blur-3xl opacity-[0.05]" style={{ backgroundColor: BRAND }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-4">
        <StatsRow t={t} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-10">

          {/* Kimlik */}
          <div className="lg:col-span-3">
            <ColumnTitle>{t('footer.contactInfo')}</ColumnTitle>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(123,13,30,0.08)', color: BRAND }}>
                  <MapPin size={15} />
                </span>
                <div className="text-sm leading-relaxed">
                  <div className="font-semibold text-gray-900">{t('footer.deanOffice')}</div>
                  <div className="text-gray-600">{t('footer.address')}</div>
                </div>
              </div>
              <a href="mailto:fendek@hacettepe.edu.tr" className="flex items-center gap-3 group">
                <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(123,13,30,0.08)', color: BRAND }}>
                  <Mail size={15} />
                </span>
                <span className="text-sm text-gray-600 group-hover:text-[#7B0D1E] transition-colors">fendek@hacettepe.edu.tr</span>
              </a>
            </div>

            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">{t('footer.followUs')}</div>
              <div className="flex gap-2.5">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.label}
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full border border-gray-300 text-gray-700 flex items-center justify-center hover:text-white hover:border-transparent hover:-translate-y-0.5 transition-all duration-300 hover:bg-[#7B0D1E] hover:shadow-lg"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bölümler */}
          <div className="lg:col-span-3">
            <ColumnTitle>{t('footer.departments')}</ColumnTitle>
            <ul className="divide-y divide-gray-200 border-y border-gray-200">
              {departments.map((d) => (
                <li key={d.href}>
                  <a
                    href={d.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 py-3.5"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 group-hover:text-[#7B0D1E] transition-colors">{d.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{d.phone}</div>
                    </div>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-gray-400 group-hover:text-white group-hover:bg-[#7B0D1E] transition-all duration-300">
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Son Duyurular */}
          <div className="lg:col-span-3">
            <LatestAnnouncements t={t} prefix={prefix} isEn={isEn} />
          </div>

          {/* İletişim + Hızlı Bağlantılar */}
          <div className="lg:col-span-3 grid grid-cols-1 gap-10">
            <div>
              <ColumnTitle>{t('footer.phone')}</ColumnTitle>
              <ul className="space-y-2.5">
                {PHONES.map((p) => (
                  <li key={p}>
                    <a href={telHref(p)} className="inline-flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#7B0D1E] transition-colors">
                      <Phone size={14} style={{ color: BRAND }} />
                      {p}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <ColumnTitle>{t('footer.quickLinks')}</ColumnTitle>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to={localizePath('/iletisim')} className="text-gray-600 hover:text-[#7B0D1E] transition-colors">
                    {t('footer.contactInfo')}
                  </Link>
                </li>
                <li>
                  <a href="https://www.hacettepe.edu.tr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gray-600 hover:text-[#7B0D1E] transition-colors">
                    {isEn ? 'Hacettepe University' : 'Hacettepe Üniversitesi'}
                    <ArrowUpRight size={13} />
                  </a>
                </li>
                <li>
                  <a href="mailto:fendek@hacettepe.edu.tr" className="text-gray-600 hover:text-[#7B0D1E] transition-colors">
                    {t('footer.contactUs')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Filigran yazı */}
      <div
        className="relative select-none pointer-events-none text-center whitespace-nowrap font-black leading-[0.78] tracking-tight -mb-[1.5vw] mt-6"
        style={{ fontSize: 'clamp(48px, 10.5vw, 170px)', color: 'rgba(123, 13, 30, 0.06)' }}
        aria-hidden="true"
      >
        {t('footer.watermark')}
      </div>
      </div>

      {/* Alt şerit */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(100deg, #3A0610 0%, ${BRAND} 55%, #8E1528 100%)` }}>
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 22px)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">

          {/* Üst kat: fakülte logosu | YÖKAK + dil + yukarı */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to={prefix || '/'} aria-label={t('footer.facultyFullName')} className="shrink-0">
              <img loading="lazy" decoding="async" src="/fenfaklogo-beyaz.png" alt={t('footer.facultyFullName')} className="h-11 sm:h-12 w-auto max-w-full" />
            </Link>

            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5">
              {INSTITUTION_LOGOS.map((l) => (
                <a key={l.alt} href={l.href} target="_blank" rel="noopener noreferrer" title={l.alt}
                   className="opacity-80 hover:opacity-100 transition-opacity">
                  <img loading="lazy" decoding="async" src={l.src} alt={l.alt} className={`${l.className} w-auto`} style={{ filter: 'brightness(0) invert(1)' }} />
                </a>
              ))}
              <span className="hidden sm:block w-px h-8 bg-white/20" aria-hidden="true" />
            {/* Dil seçimi */}
            <div className="flex items-center rounded-full border border-white/30 p-0.5">
              {['tr', 'en'].map((lang) => {
                const active = (lang === 'en') === isEn;
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => changeLanguage(lang)}
                    aria-pressed={active}
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                      active ? 'bg-white text-[#7B0D1E]' : 'text-white/75 hover:text-white'
                    }`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={scrollToTop}
              aria-label={isEn ? 'Back to top' : 'Yukarı çık'}
              className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-[#7B0D1E] transition-all duration-300"
            >
              <ArrowUp size={16} />
            </button>
            </div>
          </div>

          {/* Alt kat: telif | iletişim bağlantıları */}
          <div className="mt-6 pt-5 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-3 text-[13px] text-white/70">
            <p className="text-center md:text-left">
              © {new Date().getFullYear()} {t('footer.facultyFullName')}. {t('footer.allRightsReserved')}.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={13} className="text-white/50" />
                Beytepe, Ankara
              </span>
              <a href="mailto:fendek@hacettepe.edu.tr" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail size={13} className="text-white/50" />
                fendek@hacettepe.edu.tr
              </a>
              <a href="https://www.hacettepe.edu.tr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                hacettepe.edu.tr
                <ArrowUpRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
